import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { CompletionRecord, GameSessionLog } from '../types/progress';
import type { Subject } from '../types/activity';
import { todayStr } from '../utils/date';
import { logBetaEvent } from '../lib/beta-ops';
import { track } from '../lib/analytics';

export interface ExitTicketResult {
  correct: number;
  total: number;
  timestamp: string;
}

const MAX_GAME_ATTEMPTS = 100;
const MAX_GAME_TEXT = 180;

interface ProgressState {
  completions: CompletionRecord[];
  /**
   * Per-completion game attempt detail for parent review. Keyed by
   * completion.id so a kid can play the same game twice in a day and the
   * parent still opens the exact run from the feed row they tapped.
   */
  gameSessionLogs: Record<string, GameSessionLog>;
  /**
   * Per-child, per-activity exit-ticket outcomes. Populated via
   * `addExitTicketResult`. Intended as an input signal for future adaptive
   * difficulty and parent-dashboard "how well did they do" surfaces.
   */
  exitTicketResults: Record<string, Record<string, ExitTicketResult>>;
  /**
   * Per-completion flag set by the parent in the ActivityFeed → lesson-detail
   * sheet "Mark as discussed" toggle. Keyed by completion.id (not activityId)
   * so repeated lessons each get their own checkmark. Record over Set to keep
   * zustand-persist happy (Sets don't serialise through JSON).
   */
  parentDiscussedCompletions: Record<string, true>;
  addCompletion: (record: Omit<CompletionRecord, 'id'>) => CompletionRecord;
  addGameSessionLog: (log: Omit<GameSessionLog, 'id'>) => GameSessionLog;
  getGameSessionLogForCompletion: (completionId: string) => GameSessionLog | undefined;
  addExitTicketResult: (
    childId: string,
    activityId: string,
    result: { correct: number; total: number },
  ) => void;
  setCompletionDiscussed: (completionId: string, discussed: boolean) => void;
  getCompletionsForChild: (childId: string) => CompletionRecord[];
  getRecentActivityIds: (childId: string, days?: number) => string[];
  getTodayCompletions: (childId: string) => CompletionRecord[];
  getSubjectMinutes: (childId: string, days?: number) => Record<Subject, number>;
}

export const useProgressStore = create<ProgressState>()(
  persist(
    (set, get) => ({
      completions: [],
      gameSessionLogs: {},
      exitTicketResults: {},
      parentDiscussedCompletions: {},

      setCompletionDiscussed: (completionId, discussed) => {
        set(state => {
          const next = { ...state.parentDiscussedCompletions };
          if (discussed) {
            next[completionId] = true;
          } else {
            delete next[completionId];
          }
          return { parentDiscussedCompletions: next };
        });
      },

      addExitTicketResult: (childId, activityId, result) => {
        // (0,0) is the "no authored ticket" sentinel — ignore to keep signal clean.
        if (result.total <= 0) return;
        set(state => {
          const perChild = state.exitTicketResults[childId] ?? {};
          return {
            exitTicketResults: {
              ...state.exitTicketResults,
              [childId]: {
                ...perChild,
                [activityId]: {
                  correct: Math.max(0, Math.min(result.correct, result.total)),
                  total: result.total,
                  timestamp: new Date().toISOString(),
                },
              },
            },
          };
        });
      },

      addCompletion: (record) => {
        // Length cap at the store boundary so a tampered caller (or imported
        // file slipping past the validator) can't store unbounded text.
        const completion: CompletionRecord = {
          ...record,
          id: crypto.randomUUID(),
          photoCaption: typeof record.photoCaption === 'string'
            ? record.photoCaption.slice(0, 280)
            : record.photoCaption,
        };
        set(state => ({
          completions: [...state.completions, completion],
        }));
        logBetaEvent('activity.completed', {
          childId: completion.childId,
          activityId: completion.activityId,
          subject: completion.subject,
          starsEarned: completion.starsEarned,
          durationMinutes: completion.durationMinutes,
        });
        // Funnel signal: every completion path (dashboard, courses, library,
        // language) flows through here, so this one call powers the
        // activation metric (2+ lessons). No PII — subject only.
        track('lesson_complete', { subject: completion.subject });
        return completion;
      },

      addGameSessionLog: (log) => {
        const session = sanitizeGameSessionLog(log);
        set(state => ({
          gameSessionLogs: {
            ...state.gameSessionLogs,
            [session.completionId]: session,
          },
        }));
        return session;
      },

      getGameSessionLogForCompletion: (completionId) => {
        return get().gameSessionLogs[completionId];
      },

      getCompletionsForChild: (childId) => {
        return get().completions.filter(c => c.childId === childId);
      },

      getRecentActivityIds: (childId, days = 5) => {
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - days);
        const cutoffStr = cutoff.toISOString().split('T')[0];
        return get().completions
          .filter(c => c.childId === childId && c.date >= cutoffStr)
          .map(c => c.activityId);
      },

      getTodayCompletions: (childId) => {
        const today = todayStr();
        return get().completions.filter(c => c.childId === childId && c.date === today);
      },

      getSubjectMinutes: (childId, days = 7) => {
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - days);
        const cutoffStr = cutoff.toISOString().split('T')[0];

        const minutes: Record<string, number> = {};
        for (const c of get().completions) {
          if (c.childId === childId && c.date >= cutoffStr) {
            minutes[c.subject] = (minutes[c.subject] || 0) + c.durationMinutes;
          }
        }
        return minutes as Record<Subject, number>;
      },
    }),
    { name: 'homeschool-progress', version: 0, migrate: (state) => state }
  )
);

function sanitizeGameSessionLog(log: Omit<GameSessionLog, 'id'>): GameSessionLog {
  const attempts = log.attempts.slice(-MAX_GAME_ATTEMPTS).map(attempt => ({
    prompt: cleanGameText(attempt.prompt),
    correctAnswer: cleanGameText(attempt.correctAnswer),
    childAnswer: attempt.childAnswer ? cleanGameText(attempt.childAnswer) : undefined,
    choices: attempt.choices?.slice(0, 8).map(cleanGameText),
    isCorrect: !!attempt.isCorrect,
    skillTag: attempt.skillTag ? cleanGameText(attempt.skillTag, 80) : undefined,
    responseMs: clampOptionalNumber(attempt.responseMs),
    level: clampOptionalNumber(attempt.level),
    round: clampOptionalNumber(attempt.round),
    mode: attempt.mode ? cleanGameText(attempt.mode, 80) : undefined,
  }));

  const totalAttempts = Math.max(0, Math.round(log.summary.totalAttempts));
  const correct = Math.max(0, Math.round(log.summary.correct));
  const incorrect = Math.max(0, Math.round(log.summary.incorrect));
  const accuracy = Math.max(0, Math.min(100, Math.round(log.summary.accuracy)));
  const skillsPracticed = uniqueClean(log.summary.skillsPracticed);
  const missedSkills = uniqueClean(log.summary.missedSkills);

  return {
    ...log,
    id: crypto.randomUUID(),
    mode: log.mode ? cleanGameText(log.mode, 80) : undefined,
    summary: {
      totalAttempts,
      correct,
      incorrect,
      accuracy,
      skillsPracticed,
      missedSkills,
      score: clampOptionalNumber(log.summary.score),
      level: clampOptionalNumber(log.summary.level),
      streak: clampOptionalNumber(log.summary.streak),
    },
    attempts,
  };
}

function cleanGameText(value: string, max = MAX_GAME_TEXT): string {
  return value.replace(/\s+/g, ' ').trim().slice(0, max);
}

function uniqueClean(values: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const value of values) {
    const cleaned = cleanGameText(value, 80);
    const key = cleaned.toLowerCase();
    if (!cleaned || seen.has(key)) continue;
    seen.add(key);
    out.push(cleaned);
    if (out.length >= 12) break;
  }
  return out;
}

function clampOptionalNumber(value: number | undefined): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) return undefined;
  return Math.max(0, Math.round(value));
}
